/**
 * Currency utilities for handling different currencies in PayPal payments
 */

import { SupportedLanguage } from '../config/languageConfig';
import { MenuItem } from '../types/menu';

// Define supported currencies
export type SupportedCurrency = "USD" | "JPY" | "CNY" | "TWD" | "KRW";

// Exchange rates to USD (as of latest update)
// These should be updated regularly or fetched from an API in production
export const EXCHANGE_RATES: Record<SupportedCurrency, number> = {
  USD: 1.0, // 1 USD = 1 USD
  KRW: 0.00074, // 1 KRW = 0.00074 USD (약 1350원 = 1 USD)
  JPY: 0.0066, // 1 JPY = 0.0066 USD (약 152엔 = 1 USD)
  CNY: 0.138, // 1 CNY = 0.138 USD (약 7.24위안 = 1 USD)
  TWD: 0.031, // 1 TWD = 0.031 USD (약 32.3 대만 달러 = 1 USD)
};

// Currency symbols
export const CURRENCY_SYMBOLS: Record<SupportedCurrency, string> = {
  USD: "$",
  KRW: "₩",
  JPY: "¥",
  CNY: "¥",
  TWD: "NT$",
};

/**
 * 언어에 따른 기본 통화 매핑
 */
export const LANGUAGE_TO_CURRENCY: Record<SupportedLanguage, SupportedCurrency> = {
  'en': 'USD',
  'ko': 'KRW',
  'ja': 'JPY',
  'zh-CN': 'CNY',
  'zh-TW': 'TWD'
};

/**
 * 언어 코드에 따른 기본 통화 반환
 * @param language - 언어 코드
 * @returns 해당 언어의 기본 통화
 */
export const getCurrencyFromLanguage = (language: SupportedLanguage): SupportedCurrency => {
  return LANGUAGE_TO_CURRENCY[language] || 'USD';
};

/**
 * 장바구니 페이지에서 사용할 통화 반환 (중국어는 기술적 이유로 USD 사용)
 * @param language - 언어 코드
 * @returns 장바구니에서 사용할 통화
 */
export const getCurrencyForCart = (language: SupportedLanguage): SupportedCurrency => {
  // 중국어는 기술적 이유로 USD 사용
  if (language === 'zh-CN') {
    return 'USD';
  }
  return LANGUAGE_TO_CURRENCY[language] || 'USD';
};

/**
 * 통화에 따른 가격 필드 이름 반환
 * @param currency - 통화 코드
 * @returns 해당 통화의 가격 필드 이름
 */
export const getPriceFieldByCurrency = (currency: SupportedCurrency): string => {
  switch (currency) {
    case 'KRW': return 'priceKrw';
    case 'USD': return 'priceUsd';
    case 'JPY': return 'priceJpy';
    case 'CNY': return 'priceCny';
    case 'TWD': return 'priceTwd';
    default: return 'price';
  }
};

/**
 * Format price with currency symbol
 * @param price - Price to format
 * @param currency - Currency code
 * @returns Formatted price with currency symbol
 */
export const formatPrice = (
  price: number,
  currency: SupportedCurrency = "USD",
): string => {
  switch (currency) {
    case "USD":
      // USD는 소수점 2자리까지 표시 (센트 단위)
      return `$${price.toFixed(2)}`;
    case "JPY":
      // 일본 엔은 정수로 표시
      return `¥${Math.round(price)}`;
    case "CNY":
      // 중국 위안은 정수로 표시
      return `¥${Math.round(price)}`;
    case "TWD":
      // 대만 달러는 정수로 표시
      return `NT$${Math.round(price)}`;
    case "KRW":
      // 한국 원은 정수로 표시하고 천 단위 구분자 추가
      return `₩${Math.round(price).toLocaleString()}`;
    default:
      return `${Math.round(price)}`;
  }
};

/**
 * Convert price from source currency to target currency
 * @param price - Price to convert
 * @param sourceCurrency - Source currency code
 * @param targetCurrency - Target currency code
 * @returns Converted price
 */
export const convertCurrency = (
  price: number,
  sourceCurrency: SupportedCurrency,
  targetCurrency: SupportedCurrency,
): number => {
  // If same currency, no conversion needed
  if (sourceCurrency === targetCurrency) {
    return price;
  }
  
  // Convert to USD first, then to target currency
  const priceInUSD = price * EXCHANGE_RATES[sourceCurrency];

  if (targetCurrency === "USD") {
    return Math.round(priceInUSD * 100) / 100; // USD일 경우 센트 단위로 반올림하여 반환
  }

  const convertedPrice = priceInUSD / EXCHANGE_RATES[targetCurrency];
  
  // 각 통화에 맞게 반올림 처리
  let roundedPrice: number;
  
  // targetCurrency가 USD일 수 없음 (위에서 이미 처리됨)
  switch(targetCurrency) {
    case 'JPY':
    case 'KRW':
      roundedPrice = Math.round(convertedPrice); // 정수로 반올림
      break;
    case 'CNY':
      roundedPrice = Math.round(convertedPrice * 10) / 10; // 0.1 단위로 반올림
      break;
    case 'TWD':
      roundedPrice = Math.round(convertedPrice * 2) / 2; // 0.5 단위로 반올림
      break;
    default:
      roundedPrice = Math.round(convertedPrice);
  }
  
  return roundedPrice;
};

/**
 * 통화에 따른 프로모션 가격 필드 이름 반환
 * @param currency - 통화 코드
 * @returns 해당 통화의 프로모션 가격 필드 이름
 */
export const getPromotionPriceFieldByCurrency = (currency: SupportedCurrency): string => {
  switch (currency) {
    case 'KRW': return 'promotionPriceKrw';
    case 'USD': return 'promotionPriceUsd';
    case 'JPY': return 'promotionPriceJpy';
    case 'CNY': return 'promotionPriceCny';
    case 'TWD': return 'promotionPriceTwd';
    default: return 'promotionPrice';
  }
};

/**
 * 통화에 따른 프로모션 여부 필드 이름 반환
 * @param currency - 통화 코드
 * @returns 해당 통화의 프로모션 여부 필드 이름
 */
export const getPromotionFlagByCurrency = (currency: SupportedCurrency): string => {
  switch (currency) {
    case 'KRW': return 'isPromotionKrw';
    case 'USD': return 'isPromotionUsd';
    case 'JPY': return 'isPromotionJpy';
    case 'CNY': return 'isPromotionCny';
    case 'TWD': return 'isPromotionTwd';
    default: return 'isPromotionUsd';
  }
};

/**
 * 메뉴 아이템의 통화별 프로모션 여부 확인
 * @param menuItem - 메뉴 아이템
 * @param currency - 통화 코드
 * @returns 해당 통화의 프로모션 여부
 */
export const isPromotionActive = (menuItem: MenuItem, currency: SupportedCurrency): boolean => {
  const promotionFlag = getPromotionFlagByCurrency(currency);
  return !!(menuItem as any)[promotionFlag];
};

/**
 * 메뉴 아이템의 통화별 프로모션 가격 가져오기
 * @param menuItem - 메뉴 아이템
 * @param currency - 통화 코드
 * @returns 해당 통화의 프로모션 가격
 */
export const getPromotionPrice = (menuItem: MenuItem, currency: SupportedCurrency): number => {
  const promotionPriceField = getPromotionPriceFieldByCurrency(currency);
  return (menuItem as any)[promotionPriceField] || 0;
};

/**
 * 메뉴 아이템의 통화별 일반 가격 가져오기
 * @param menuItem - 메뉴 아이템
 * @param currency - 통화 코드
 * @returns 해당 통화의 일반 가격
 */
export const getRegularPrice = (menuItem: MenuItem, currency: SupportedCurrency): number => {
  const priceField = getPriceFieldByCurrency(currency);
  return (menuItem as any)[priceField] || menuItem.price || 0;
};

/**
 * 프로모션 정보를 포함한 가격 정보 반환
 */
export interface PriceInfo {
  hasPromotion: boolean;
  regularPrice: number;
  promotionPrice: number;
  promotionRatio: number;
  formattedRegularPrice: string;
  formattedPromotionPrice: string;
  formattedDiscountRate: string;
}

/**
 * 프로모션 정보를 포함한 가격 정보 반환
 * @param menuItem - 메뉴 아이템
 * @param currency - 통화 코드
 * @returns 프로모션 정보를 포함한 가격 정보
 */
export const getPriceInfo = (menuItem: MenuItem, currency: SupportedCurrency): PriceInfo => {
  const hasPromotion = isPromotionActive(menuItem, currency);
  const regularPrice = getRegularPrice(menuItem, currency);
  const promotionPrice = hasPromotion ? getPromotionPrice(menuItem, currency) : 0;
  const promotionRatio = menuItem.promotionRatio || 0;
  
  return {
    hasPromotion,
    regularPrice,
    promotionPrice,
    promotionRatio,
    formattedRegularPrice: formatPrice(regularPrice, currency),
    formattedPromotionPrice: hasPromotion ? formatPrice(promotionPrice, currency) : '',
    formattedDiscountRate: hasPromotion ? `${Math.round(promotionRatio * 100)}%` : ''
  };
};
